import { agentDisplayAssociations, formatModelLabel, getGitRepoAttachment } from '@reflex/shared';
import { normalizeRepoSlug } from '@reflex/ui/lib/repo-slug';
import { getDraftMeta } from '@/components/agent/draft-meta';
import type { Agent, AgentDraft } from '@/lib/types';

/**
 * Split a raw search-box value into the terms a row has to match. Terms are
 * lowercased and whitespace-separated, and every one of them has to hit
 * *some* field of the row (`claude reflex` finds the claude-code runs on the
 * reflex repo). A blank query yields no terms, which matches everything.
 */
export function searchTerms(query: string | null | undefined): string[] {
  const trimmed = query?.trim().toLowerCase() ?? '';
  return trimmed.length === 0 ? [] : trimmed.split(/\s+/);
}

/** True when every term appears in at least one of the row's fields. */
export function matchesSearchTerms(
  fields: readonly (string | null | undefined)[],
  terms: readonly string[],
): boolean {
  if (terms.length === 0) return true;
  const haystack = fields
    .filter((f): f is string => typeof f === 'string' && f.length > 0)
    .map((f) => f.toLowerCase());
  return terms.every((term) => haystack.some((field) => field.includes(term)));
}

/** Both spellings of a repo reference: the stored value and its bare slug. */
function repoFields(repo: string | null | undefined): string[] {
  if (!repo) return [];
  return [repo, normalizeRepoSlug(repo)];
}

/**
 * Everything about a run that the sidebar puts on screen, as searchable text.
 *
 * Search should match what the row shows: the title, the agent type, the
 * model, the repo and branch, and any linked pull request or issue. Matching
 * only `name` and `prompt` made the box feel broken — typing the repo or the
 * model you were looking at returned nothing. The prompt and the run id stay
 * in the set even though they are not rendered, so pasting an id from a link
 * or recalling a phrase from the launch prompt still finds the run.
 */
export function agentSearchFields(agent: Agent): string[] {
  const repo = getGitRepoAttachment(agent);
  const fields: (string | null | undefined)[] = [
    agent.name,
    agent.prompt,
    agent.id,
    agent.agentType,
    agent.model,
    agent.model ? formatModelLabel(agent.model) : null,
    ...repoFields(repo?.repoSlug),
    repo?.repoBranch,
    agent.status,
  ];
  for (const ref of agentDisplayAssociations(agent)) {
    // `owner/repo#42` carries both the repo and the number, so searching
    // "#42" or the repo name finds the run through its PR.
    fields.push(ref.externalId);
    const title = ref.data?.title;
    if (typeof title === 'string') fields.push(title);
  }
  return fields.filter((f): f is string => typeof f === 'string' && f.length > 0);
}

/** The draft equivalent of {@link agentSearchFields}, over the saved payload. */
export function draftSearchFields(draft: AgentDraft): string[] {
  const meta = getDraftMeta(draft);
  const fields: (string | null | undefined)[] = [
    draft.payload.initialPrompt,
    draft.payload.prompt,
    draft.payload.name,
    meta.agentType,
    meta.model,
    ...repoFields(meta.repoSlug),
    meta.branch,
  ];
  return fields.filter((f): f is string => typeof f === 'string' && f.length > 0);
}

/** True when a run matches every term of the current query. */
export function agentMatchesSearch(agent: Agent, terms: readonly string[]): boolean {
  return terms.length === 0 || matchesSearchTerms(agentSearchFields(agent), terms);
}

/** True when a draft matches every term of the current query. */
export function draftMatchesSearch(draft: AgentDraft, terms: readonly string[]): boolean {
  return terms.length === 0 || matchesSearchTerms(draftSearchFields(draft), terms);
}
